// PrestoSinglePlayer.jsx
import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { LoadMode, PRIORITY } from '../resource/playerConfig.js';
import createPlayer from '../resource/createPlayer.js';
import createVideoElement from '../resource/createVideoElement.js';
import createPlayerControl from '../resource/createPlayerControl.js';
import { getPlayerPool } from '../resource/VodPlayerPool.js';
import { getVideoElementPool } from '../resource/VideoElementPool.js';
import { NULL_FUNCTION } from '../resource/defaults.js';
import { player as playerDebug } from '../resource/debug.js';
import { PlayerContext } from '../context/PlayerContext.js';
import {
  hasValidSourceUrl,
  reportPlayerCallbackDeclarationMismatches,
} from '../resource/playerUtils.js';

const singlePlayerLog = playerDebug.extend('log:PrestoSinglePlayer');

/** Single-instance VOD player. Callbacks only fire when loadMode === ACTIVE (engagement gate). */
const PrestoSinglePlayer = ({
  messageId,
  source,
  drm = null,
  drmFeatureName = '',
  isNativePlayback = false,
  loadMode = LoadMode.NONE,
  autoplay = false,
  isMuted = true,
  loop = false,
  isBrowserTabVisible = true,
  onSetup = NULL_FUNCTION,
  onEnded = NULL_FUNCTION,
  onWaiting = NULL_FUNCTION,
  onPlay = NULL_FUNCTION,
  onPlaying = NULL_FUNCTION,
  onPause = NULL_FUNCTION,
  onCanPlay = NULL_FUNCTION,
  onProgressUpdate = NULL_FUNCTION,
  onError = NULL_FUNCTION,
  onLoadedMetadata = NULL_FUNCTION,
  onCanplaythrough = NULL_FUNCTION,
  onLoadStart = NULL_FUNCTION,
  onLoadedData = NULL_FUNCTION,
  onSeeking = NULL_FUNCTION,
  onSeeked = NULL_FUNCTION,
  onProgress = NULL_FUNCTION,
  onVolumeChange = NULL_FUNCTION,
  onEnterPictureInPicture = NULL_FUNCTION,
  onTimeupdate = NULL_FUNCTION,
  children = null,
}) => {
  const playerContext = useContext(PlayerContext);
  const setContextPlayer = playerContext?.setPlayer;
  const setContextPlayerRef = useRef(setContextPlayer);
  setContextPlayerRef.current = setContextPlayer;

  const playerContainerRef = useRef(null);
  const [player, setPlayer] = useState(null);
  const playerRef = useRef(null);
  const playerControlRef = useRef(null);

  const isActive = loadMode === LoadMode.ACTIVE;
  const hasValidSource = hasValidSourceUrl(source);
  const shouldLoadPlayer = loadMode !== LoadMode.NONE && hasValidSource;

  const playerOptionsRef = useRef({});
  playerOptionsRef.current = {
    loadMode,
    isActive,
    isMuted,
    loop,
    autoplay,
    isNativePlayback,
  };

  const sourceConfigRef = useRef({ source, drm, drmFeatureName });
  sourceConfigRef.current = { source, drm, drmFeatureName };

  const callbacksRef = useRef({});
  callbacksRef.current = {
    onSetup,
    onEnded,
    onWaiting,
    onPlay,
    onPlaying,
    onPause,
    onCanPlay,
    onProgressUpdate,
    onError,
    onLoadedMetadata,
    onCanplaythrough,
    onLoadStart,
    onLoadedData,
    onSeeking,
    onSeeked,
    onProgress,
    onVolumeChange,
    onEnterPictureInPicture,
    onTimeupdate,
  };
  reportPlayerCallbackDeclarationMismatches({
    componentName: 'PrestoSinglePlayer',
    callbacks: callbacksRef.current,
    propTypes: PrestoSinglePlayer.propTypes,
  });

  const hasNotifiedPlayingRef = useRef(false);
  const hasFiredSetupRef = useRef(false);

  const runWhenPlayerIsActive =
    callback =>
    (...args) => {
      if (!playerOptionsRef.current.isActive) return;
      callback(...args);
    };

  const reloadActivePlayer = () => {
    const activePlayer = playerRef.current;
    if (!activePlayer || !playerOptionsRef.current.isActive) return;

    callbacksRef.current.onWaiting?.();
    activePlayer
      .load()
      .then(() => {
        if (!playerOptionsRef.current.isActive) return;
        activePlayer.setMuted(playerOptionsRef.current.isMuted);
        return activePlayer.play();
      })
      .catch(NULL_FUNCTION);
  };

  useEffect(() => {
    if (!shouldLoadPlayer || !messageId) {
      if (messageId) {
        const currentLoadMode = playerOptionsRef.current.loadMode;
        const reason = !hasValidSource
          ? 'no source manifest'
          : currentLoadMode === LoadMode.NONE
            ? 'loadMode is NONE'
            : 'unknown';
        singlePlayerLog(`(msgId ${messageId}) player not created: ${reason}`, {
          loadMode: currentLoadMode,
        });
      }
      setPlayer(null);
      return undefined;
    }

    singlePlayerLog(`(msgId ${messageId}) player created`, {
      loadMode: playerOptionsRef.current.loadMode,
    });

    const notifyReadyToPlayCallbacks = () => {
      const control = playerControlRef.current?.control;
      callbacksRef.current.onCanPlay?.({ type: 'canplay' }, control);
      callbacksRef.current.onCanplaythrough?.({ type: 'canplaythrough' });
    };

    const createPlayerFn = playerOptionsRef.current.isNativePlayback
      ? createVideoElement
      : createPlayer;
    const nextPlayer = createPlayerFn({
      vodPlayerPool: getPlayerPool(),
      videoElementPool: getVideoElementPool(),
      source: {
        id: messageId,
        src: sourceConfigRef.current.source,
        drm: sourceConfigRef.current.drm,
        drmFeatureName: sourceConfigRef.current.drmFeatureName,
      },
      priority: playerOptionsRef.current.isActive
        ? PRIORITY.ACTIVE
        : PRIORITY.PRELOAD,
      playerContainerRef,
      debugLabel: `single-${messageId}`,
      shouldLoop: () => playerOptionsRef.current.loop,
      onEnded: () => callbacksRef.current.onEnded?.(),
      onWaiting: () => callbacksRef.current.onWaiting?.(),
      onError: error => callbacksRef.current.onError?.(error),
      onCanPlay: runWhenPlayerIsActive(() => {
        notifyReadyToPlayCallbacks();
      }),
      onProgressUpdate: runWhenPlayerIsActive(payload => {
        callbacksRef.current.onProgressUpdate?.(payload);
      }),
      onPlaying: runWhenPlayerIsActive(payload => {
        if (hasNotifiedPlayingRef.current) {
          if (callbacksRef.current.onPlay === NULL_FUNCTION) {
            callbacksRef.current.onPlaying?.(payload);
          }
          return;
        }
        hasNotifiedPlayingRef.current = true;
        callbacksRef.current.onPlay?.(payload);
        callbacksRef.current.onPlaying?.(payload);
      }),
      onPause: runWhenPlayerIsActive(() => {
        hasNotifiedPlayingRef.current = false;
        callbacksRef.current.onPause?.();
      }),
      onLoadedMetadata: event => {
        callbacksRef.current.onLoadedMetadata?.(
          event,
          playerControlRef.current?.control
        );
      },
      onCanplaythrough: event =>
        callbacksRef.current.onCanplaythrough?.(
          event,
          playerControlRef.current?.control
        ),
      onLoadStart: runWhenPlayerIsActive(event => {
        callbacksRef.current.onLoadStart?.(
          event,
          playerControlRef.current?.control
        );
      }),
      onLoadedData: runWhenPlayerIsActive(event => {
        callbacksRef.current.onLoadedData?.(
          event,
          playerControlRef.current?.control
        );
      }),
      onSeeking: runWhenPlayerIsActive(event => {
        callbacksRef.current.onSeeking?.(
          event,
          playerControlRef.current?.control
        );
      }),
      onSeeked: runWhenPlayerIsActive(event => {
        callbacksRef.current.onSeeked?.(
          event,
          playerControlRef.current?.control
        );
      }),
      onProgress: runWhenPlayerIsActive(event => {
        callbacksRef.current.onProgress?.(
          event,
          playerControlRef.current?.control
        );
      }),
      onVolumeChange: runWhenPlayerIsActive(() => {
        callbacksRef.current.onVolumeChange?.();
      }),
      onEnterPictureInPicture: runWhenPlayerIsActive(() => {
        callbacksRef.current.onEnterPictureInPicture?.();
      }),
      onTimeupdate: runWhenPlayerIsActive(event => {
        callbacksRef.current.onTimeupdate?.(
          event,
          playerControlRef.current?.control
        );
      }),
      onPreempted: () => {
        hasNotifiedPlayingRef.current = false;
        reloadActivePlayer();
      },
    });
    playerRef.current = nextPlayer;

    const controller = createPlayerControl({
      getActivePlayer: () => playerRef.current,
      getContainerElement: () => playerContainerRef.current,
      getSource: () => sourceConfigRef.current,
      getCallbacks: () => callbacksRef.current,
    });
    playerControlRef.current = controller;

    const snapshotUnsubscribe = nextPlayer.subscribe(controller.syncListeners);
    setPlayer(nextPlayer);
    // Player SDK resets muted during load; re-apply and play explicitly
    // after load completes — mirrors PrestoDualPlayer's playWithCurrentMute.
    nextPlayer
      .load()
      .then(() => {
        if (!playerOptionsRef.current.isActive) return;
        nextPlayer.setMuted(playerOptionsRef.current.isMuted);
        return nextPlayer.play();
      })
      .catch(NULL_FUNCTION);

    return () => {
      snapshotUnsubscribe();
      controller.dispose();
      playerControlRef.current = null;
      nextPlayer.release();
      playerRef.current = null;
      hasNotifiedPlayingRef.current = false;
      hasFiredSetupRef.current = false;
      setPlayer(null);
    };
  }, [messageId, shouldLoadPlayer, isNativePlayback, hasValidSource]);

  useEffect(() => {
    if (!player) return;
    player.update({
      source: { id: messageId, src: source, drm, drmFeatureName },
      loadMode: shouldLoadPlayer ? loadMode : LoadMode.NONE,
      isBrowserTabVisible,
      autoplay,
    });
  }, [
    player,
    messageId,
    source,
    drm,
    drmFeatureName,
    loadMode,
    shouldLoadPlayer,
    autoplay,
    isBrowserTabVisible,
  ]);

  useEffect(() => {
    getPlayerPool().syncMutedState(isMuted);
    if (isNativePlayback) {
      playerRef.current?.setMuted(isMuted);
    }
  }, [isMuted, isNativePlayback]);

  // video.loop on the native element conflicts with MSE ended
  // detection — loop is handled by shouldLoop callback in createPlayer.
  useEffect(() => {
    if (!player || !isActive) return;
    hasFiredSetupRef.current = true;
    const control = playerControlRef.current?.control;
    callbacksRef.current.onSetup?.(control);
    setContextPlayerRef.current?.(control);
    return () => {
      setContextPlayerRef.current?.(null);
      hasFiredSetupRef.current = false;
    };
  }, [player, isActive]);

  return (
    <PlayerContainer ref={playerContainerRef} data-message-id={messageId}>
      {children}
    </PlayerContainer>
  );
};

PrestoSinglePlayer.propTypes = {
  messageId: PropTypes.string.isRequired,
  source: PropTypes.object,
  drm: PropTypes.object,
  drmFeatureName: PropTypes.string,
  isNativePlayback: PropTypes.bool,
  loadMode: PropTypes.oneOf(Object.values(LoadMode)),
  autoplay: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  isMuted: PropTypes.bool,
  loop: PropTypes.bool,
  isBrowserTabVisible: PropTypes.bool,
  onSetup: PropTypes.func,
  onEnded: PropTypes.func,
  onWaiting: PropTypes.func,
  onPlay: PropTypes.func,
  onPlaying: PropTypes.func,
  onPause: PropTypes.func,
  onCanPlay: PropTypes.func,
  onProgressUpdate: PropTypes.func,
  onError: PropTypes.func,
  onLoadedMetadata: PropTypes.func,
  onCanplaythrough: PropTypes.func,
  onLoadStart: PropTypes.func,
  onLoadedData: PropTypes.func,
  onSeeking: PropTypes.func,
  onSeeked: PropTypes.func,
  onProgress: PropTypes.func,
  onVolumeChange: PropTypes.func,
  onEnterPictureInPicture: PropTypes.func,
  onTimeupdate: PropTypes.func,
  children: PropTypes.node,
};

const PlayerContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  > div {
    width: 100%;
    height: 100%;
  }
`;

export default PrestoSinglePlayer;
